import CryptoJS from 'crypto-js';

const LEGACY_DEV_KEY = 'tauris-offline-dev-key';
let warnedMissingEnv = false;

/**
 * Derive offline encryption key from env salt + authenticated user id.
 * Avoids shipping a single shared key for all tenants when JWT is present.
 */
export function getOfflineEncryptionKey() {
    const envKey = process.env.REACT_APP_OFFLINE_ENCRYPTION_KEY || '';
    let userPart = '';
    try {
        if (typeof localStorage !== 'undefined') {
            const token = localStorage.getItem('token');
            if (token && token.includes('.')) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                userPart = String(payload.id || payload._id || payload.sub || payload.email || '');
            }
        }
    } catch {
        userPart = '';
    }

    if (userPart) {
        const salt = envKey || 'tauris-offline';
        return CryptoJS.SHA256(`${salt}:${userPart}`).toString();
    }

    if (envKey) return envKey;

    if (!warnedMissingEnv && typeof console !== 'undefined') {
        warnedMissingEnv = true;
        console.warn(
            '[offline] REACT_APP_OFFLINE_ENCRYPTION_KEY is unset and no auth token is available; using legacy dev key. Set the env var in production.'
        );
    }
    return LEGACY_DEV_KEY;
}

export function getLegacyOfflineEncryptionKeys() {
    const keys = [LEGACY_DEV_KEY];
    const envKey = process.env.REACT_APP_OFFLINE_ENCRYPTION_KEY;
    if (envKey) keys.push(envKey);
    return keys;
}

export function encryptPayload(data, key = getOfflineEncryptionKey()) {
    try {
        return CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
    } catch {
        return JSON.stringify(data);
    }
}

export function decryptPayload(cipher, primaryKey = getOfflineEncryptionKey()) {
    if (cipher == null) return null;
    const tryKey = (key) => {
        try {
            const bytes = CryptoJS.AES.decrypt(cipher, key);
            const json = bytes.toString(CryptoJS.enc.Utf8);
            if (!json) return null;
            return JSON.parse(json);
        } catch {
            return null;
        }
    };
    const primary = tryKey(primaryKey);
    if (primary != null) return primary;
    for (const legacy of getLegacyOfflineEncryptionKeys()) {
        if (legacy === primaryKey) continue;
        const decoded = tryKey(legacy);
        if (decoded != null) return decoded;
    }
    try {
        return JSON.parse(cipher);
    } catch {
        return null;
    }
}

/** Read JWT exp (ms) if present. */
export function getTokenExpiryMs() {
    try {
        const token = localStorage.getItem('token');
        if (!token || !token.includes('.')) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp) return null;
        return Number(payload.exp) * 1000;
    } catch {
        return null;
    }
}

export function isTokenExpiringSoon(withinMs = 60 * 60 * 1000) {
    const exp = getTokenExpiryMs();
    if (!exp) return false;
    return exp - Date.now() < withinMs;
}

export function isTokenExpired() {
    const exp = getTokenExpiryMs();
    if (!exp) return false;
    return Date.now() >= exp;
}
